import { CustomModal, FormEntry } from "components";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Row, Col } from "reactstrap";
import apiService from "services/api";
import { useCallback, useEffect, useState } from "react";
import { timeDifference } from "utils/timeDifference";

export const FormGroup = ({ children, formLabel, customEndComponent, error, light = false, width = "w-100" }) => (
	<div className={`mb-3 ${width}`}>
		<div className={`${light && "text-dark"} text-left ml-2 mb-1 text-light font-weight-bold d-flex justify-content-between align-items-center`}>
			{formLabel}
			{customEndComponent}
		</div>
		{children}
		{error && (
			<div className="text-center">
				<small className="text-danger">{error?.message}</small>
			</div>
		)}
	</div>
);

const NewAvailabilityModal = ({ state, dispatch, refetch }) => {
	const {
		register,
		reset,
		handleSubmit,
		formState: { errors },
		getValues,
		setValue,
	} = useForm({ mode: "onChange" });

	const [isRecurring, setIsRecurring] = useState(false);

	const hideModal = () => {
		reset();
		setIsRecurring(false);
		dispatch({ type: "close" });
	};

	const { mutate, isLoading } = useMutation(
		(formData) => {
			apiService.staff.availability.create(formData);
		},
		{
			onSuccess: () => {
				hideModal();
				refetch();
			},
		}
	);

	return (
		<CustomModal
			title="Staff Availability"
			show={state?.open}
			hideModal={hideModal}
			compact={true}
			buttons={
				<div className="d-flex justify-between">
					<button className="btn btn-primary" type="submit" disabled={isLoading} form="newAvailabilityForm">
						Add
					</button>
					<button className="btn btn-secondary" onClick={hideModal} disabled={isLoading} form="newAvailabilityForm">
						Cancel
					</button>
				</div>
			}
		>
			<form onSubmit={handleSubmit((data) => mutate(data))} id="newAvailabilityForm">
				<Row className="align-items-center">
					<Col lg={6}>
						<FormEntry formLabel="Date" register={register} name="date" type="date" required={true} error={errors.date} />
					</Col>
					<Col lg={6}>
						<FormEntry
							formLabel="Start Time"
							register={register}
							name="startTime"
							type="time"
							step="60"
							error={errors.startTime}
							required={true}
						/>
					</Col>
					<Col lg={6}>
						<FormEntry
							formLabel="End Time"
							register={register}
							name="endTime"
							type="time"
							validation={{
								validate: {
									checkMinMax: (preparationEndTime) =>
										timeDifference(getValues("startTime"), preparationEndTime) > 0 || "End time must be after start time",
								},
							}}
							error={errors.endTime}
							step="60"
							required={true}
						/>
					</Col>
				</Row>
				<div className="row align-items-center px-2">
					<div className="col-12 col-sm-4">
						<div className={`form-check mt-0`}>
							<label className="form-check-label">
								<input
									className="form-check-input"
									type="checkbox"
									onChange={(e) => {
										if (e.target.checked) {
											setValue("recurrType", "day");
										}
										setIsRecurring(e.target.checked);
									}}
									checked={isRecurring}
									defaultChecked={false}
								/>
								<span className="form-check-sign"></span>
								Is recurring?
							</label>
						</div>
					</div>
					{isRecurring && (
						<>
							<div className="col-6 col-sm-4">
								<div className="mr-2 d-flex align-items-center">
									<input type="radio" {...register("recurrType", { required: true })} value="day" id="recurrenceTypeDaily" />
									<label className="text-primary ml-1 mt-1" htmlFor="recurrenceTypeDaily">
										<small>Daily recurrence</small>
									</label>
								</div>
							</div>
							<div className="col-6 col-sm-4">
								<div className="d-flex align-items-center">
									<input type="radio" {...register("recurrType", { required: true })} value="week" id="recurrenceTypeWeekly" />
									<label className="text-primary ml-1 mt-1" htmlFor="recurrenceTypeWeekly">
										<small>Weekly Recurrence</small>
									</label>
								</div>
							</div>
							<div className="col-12 col-sm-6 mt-2">
								<FormEntry
									formLabel="Recurr Until"
									register={register}
									name="recurrUntill"
									type="date"
									error={errors.recurrUntill}
									required={isRecurring}
								/>
							</div>
						</>
					)}
				</div>
			</form>
		</CustomModal>
	);
};

export default NewAvailabilityModal;
